import '@archetype-themes/scripts/config'
import '@archetype-themes/scripts/modules/cart-api'
import QtySelector from '@archetype-themes/scripts/modules/quantity-selectors'
import '@archetype-themes/scripts/helpers/currency'

/*============================================================================
  CartForm
  - Prevent checkout when terms checkbox exists
  - Listen to quantity changes, rebuild cart (both widget and page)
==============================================================================*/
export default class CartForm {
  constructor(form) {
    this.selectors = {
      products: '[data-products]',
      qtySelector: '.js-qty__wrapper',
      discounts: '[data-discounts]',
      savings: '[data-savings]',
      subTotal: '[data-subtotal]',

      cartBubble: '.cart-link__bubble',
      cartNote: '[name="note"]',
      termsCheckbox: '.cart__terms-checkbox',
      checkoutBtn: '.cart__checkout'
    }

    this.classes = {
      btnLoading: 'btn--loading'
    }

    this.config = {
      requiresTerms: false
    }

    if (!form) {
      return
    }

    this.form = form
    this.wrapper = form.parentNode
    this.location = form.dataset.location
    this.namespace = '.cart-' + this.location
    this.products = form.querySelector(this.selectors.products)
    this.submitBtn = form.querySelector(this.selectors.checkoutBtn)

    this.discounts = form.querySelector(this.selectors.discounts)
    this.savings = form.querySelector(this.selectors.savings)
    this.subtotal = form.querySelector(this.selectors.subTotal)
    this.termsCheckbox = form.querySelector(this.selectors.termsCheckbox)
    this.noteInput = form.querySelector(this.selectors.cartNote)

    this.cartItemsUpdated = false

    if (this.termsCheckbox) {
      this.config.requiresTerms = true
    }

    this.init()
  }

  init() {
    this.initQtySelectors()

    document.addEventListener('cart:quantity' + this.namespace, this.quantityChanged.bind(this))

    this.form.on('submit' + this.namespace, this.onSubmit.bind(this))

    if (this.noteInput) {
      this.noteInput.addEventListener('change', function () {
        const newNote = this.value
        theme.cart.updateNote(newNote)
      })
    }

    // Dev-friendly way to build the cart
    document.addEventListener(
      'cart:build',
      function () {
        this.buildCart()
      }.bind(this)
    )
    
  }

  reInit() {
    this.initQtySelectors()
  }

  onSubmit(evt) {
    this.submitBtn.classList.add(this.classes.btnLoading)

    /*
      Checks for drawer or cart open class on body element
      and then stops the form from being submitted. We are also
      checking against a custom property, this.cartItemsUpdated = false.

      Error is handled in the quantityChanged method

      For Expanse/Fetch/Gem/Vino quick add, if an error is present it is alerted
      through the add to cart fetch request in quick-add.js.

      Custom property this.cartItemsUpdated = false is reset in cart-drawer.js for
      Expanse/Fetch/Gem/Vino when using quick add
    */

    if (
      (document.documentElement.classList.contains('js-drawer-open') && this.cartItemsUpdated) ||
      (document.documentElement.classList.contains('cart-open') && this.cartItemsUpdated)
    ) {
      this.submitBtn.classList.remove(this.classes.btnLoading)
      evt.preventDefault()
      return false
    }

    if (this.config.requiresTerms) {
      if (this.termsCheckbox.checked) {
        // continue to checkout
      } else {
        alert(theme.strings.cartTermsConfirmation)
        this.submitBtn.classList.remove(this.classes.btnLoading)
        evt.preventDefault()
        return false
      }
    }
  }

  /*============================================================================
    Query cart page to get markup
  ==============================================================================*/
  _parseProductHTML(text) {
    const html = document.createElement('div')
    html.innerHTML = text

    return {
      items: html.querySelector('.cart__items'),
      discounts: html.querySelector('.cart__discounts')
    }
  }

  buildCart() {
    return theme.cart.getCartProductMarkup().then(this.cartMarkup.bind(this))
  }

  cartMarkup(text) {
    const markup = this._parseProductHTML(text)
    const items = markup.items
    const count = parseInt(items.dataset.count)
    const subtotal = items.dataset.cartSubtotal
    const savings = items.dataset.cartSavings

    this.updateCartDiscounts(markup.discounts)
    this.updateSavings(savings)

    // Shipping prograce bar calculator with a target price of $75    
    if (theme.spb.isEnabled) {
      this.calculateProgress(subtotal, 7500)
    }

    if (count > 0) {
      this.wrapper.classList.remove('is-empty')
    } else {
      this.wrapper.classList.add('is-empty')
    }

    this.updateCount(count)    

    // Append item markup
    this.products.innerHTML = ''
    this.products.append(items)

    // Update subtotal
    this.subtotal.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat)

    this.reInit()

    if (Shopify && Shopify.StorefrontExpressButtons) {
      Shopify.StorefrontExpressButtons.initialize()
    }

    //upsell product call
    if (theme.upsell.isEnabled) {
      this.upsellInCart();
    }

    //free gift product in cart call
    if (theme.fgincart.isEnabled) {
      this.freeGiftInCart();
    }    
        
  }

  updateCartDiscounts(markup) {
    if (!this.discounts) {
      return
    }
    this.discounts.innerHTML = ''
    this.discounts.append(markup)
  }

  // Cart shipping progress bar
  calculateProgress(currentVal, targetVal) {
    var progressBar = document.querySelectorAll(
      ".cart-shippingThreshold__progress"
    );
    var progressNum = document.querySelectorAll(".cart-shipping__num");
    var progressOuter = document.querySelectorAll(
      ".cart-shipping__numOuter"
    );
    var successMsg = document.querySelectorAll(".cart-shipping__success");
    var result = Math.round((100 * currentVal) / targetVal);
    progressBar.forEach(function (el) {
      el.setAttribute("style", "width: ".concat(result, "%"));
    });
    var newProgressNum = (targetVal - currentVal) / 100;
    if (newProgressNum <= 0) {
      successMsg.forEach(function (el) {
        el.setAttribute("style", "display: block;");
      });
      progressOuter.forEach(function (el) {
        el.setAttribute("style", "display: none;");
      });
      progressNum.forEach(function (el) {
        el.textContent = newProgressNum;
      });
    } else {
      successMsg.forEach(function (el) {
        el.setAttribute("style", "display: none;");
      });
      progressOuter.forEach(function (el) {
        el.setAttribute("style", "display: block;");
      });
      progressNum.forEach(function (el) {
        el.textContent = newProgressNum.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        });
      });
    }
  }

  /*============================================================================
    Quantity handling
  ==============================================================================*/
  initQtySelectors() {
    this.form.querySelectorAll(this.selectors.qtySelector).forEach((el) => {
      const selector = new QtySelector(el, {
        namespace: this.namespace,
        isCart: true
      })
    })
  }

  quantityChanged(evt) {
    const key = evt.detail[0]
    const qty = evt.detail[1]
    const el = evt.detail[2]

    if (!key) {
      return
    }

    // Disable qty selector so multiple clicks can't happen while loading
    if (el) {
      el.classList.add('is-loading')
    }

    theme.cart
      .changeItem(key, qty)
      .then(
        function (cart) {
          const parsedCart = JSON.parse(cart)

          if (parsedCart.status === 422) {
            alert(parsedCart.message)
          } else {
            const updatedItem = parsedCart.items.find((item) => item.key === key)

            // Update cartItemsUpdated property on object so we can reference later
            if (
              updatedItem &&
              (evt.type === 'cart:quantity.cart-cart-drawer' || evt.type === 'cart:quantity.cart-header')
            ) {
              this.cartItemsUpdated = true
            }

            if (
              (updatedItem && evt.type === 'cart:quantity.cart-cart-drawer') ||
              (updatedItem && evt.type === 'cart:quantity.cart-header')
            ) {
              if (updatedItem.quantity !== qty) {
              }
              // Reset property on object so that checkout button will work as usual
              this.cartItemsUpdated = false
            }

            if (parsedCart.item_count > 0) {
              this.wrapper.classList.remove('is-empty')
            } else {
              this.wrapper.classList.add('is-empty')
            }
          }

          this.buildCart()

          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              detail: {
                cart: parsedCart
              }
            })
          )
        }.bind(this)
      )
      .catch(function (XMLHttpRequest) {})
  }

  /*============================================================================
    Update elements of the cart
  ==============================================================================*/
  updateSubtotal(subtotal) {
    this.form.querySelector(this.selectors.subTotal).innerHTML = theme.Currency.formatMoney(
      subtotal,
      theme.settings.moneyFormat
    )
  }

  updateSavings(savings) {
    if (!this.savings) {
      return
    }

    if (savings > 0) {
      const amount = theme.Currency.formatMoney(savings, theme.settings.moneyFormat)
      this.savings.classList.remove('hide')
      this.savings.innerHTML = theme.strings.cartSavings.replace('[savings]', amount)
    } else {
      this.savings.classList.add('hide')
    }
  }

  updateCount(count) {
    const countEls = document.querySelectorAll('.cart-link__bubble-num')

    if (countEls.length) {
      countEls.forEach((el) => {
        el.innerText = count
      })
    }

    // show/hide bubble(s)
    const bubbles = document.querySelectorAll(this.selectors.cartBubble)
    if (bubbles.length) {
      if (count > 0) {
        bubbles.forEach((b) => {
          b.classList.add('cart-link__bubble--visible')
        })
      } else {
        bubbles.forEach((b) => {
          b.classList.remove('cart-link__bubble--visible')
        })
      }
    }
  }





  // get cart data
  getCartData = async () => {
    let cartdata = await fetch("/cart.js", {
      method: "GET",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        return data;
      });
    return cartdata;
  }

  // add cart item
  async addItem(key, qty) {
    await fetch("/cart/add.js", {
      method: "POST",
      body: JSON.stringify({
        id: key,
        quantity: qty,
      }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((data) => {
        document.dispatchEvent(new CustomEvent("cart:build"));
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  // add cart item with properties
  async addItemWithProperty(data) {
    await fetch("/cart/add.js", {
      method: "POST",
      body: data,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    })
      .then((data) => {
        document.dispatchEvent(new CustomEvent("cart:build"));
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

    

  // upsell in cart
  upsellInCart(cart) {
    if (typeof cart == "undefined") {
      (async () => {
        this.upsellInCart(await this.getCartData());
      })();
      return;
    }

    const items_handle = cart.items;
    var pd1_validate = false;
    var pd2_validate = false;
    var pd3_validate = false;

    const upsell_pd_1 = document.getElementById("upsell_pd_1"),
      upsell_pd_2 = document.getElementById("upsell_pd_2"),
      upsell_pd_3 = document.getElementById("upsell_pd_3"),
      upsell_title = document.querySelector(".upsell_cart_title");

    const pd_1_hdl = theme.upsell.product_upsell_1,
      pd_2_hdl = theme.upsell.product_upsell_2,
      pd_3_hdl = theme.upsell.product_upsell_3;
    const all_pd_hdl = [pd_1_hdl, pd_2_hdl, pd_3_hdl];
    var active_hdl = 0;
    var active_hdl_in_cart = 0;

    items_handle.forEach((item) => {
      if (item.handle == theme.upsell.product_upsell_1) {
        pd1_validate = true;
        active_hdl_in_cart += 1;
      }
      if (item.handle == theme.upsell.product_upsell_2) {
        pd2_validate = true;
        active_hdl_in_cart += 1;
      }
      if (item.handle == theme.upsell.product_upsell_3) {
        pd3_validate = true;
        active_hdl_in_cart += 1;
      }
    });

    all_pd_hdl.forEach((hdl) => {
      if (hdl !== "" && hdl !== null) {
        active_hdl += 1;
      }
    });

    if (pd1_validate == true) {
      upsell_pd_1.classList.add("hide");
    } else if (pd1_validate == false && pd_1_hdl !== "" && pd_1_hdl !== null) {
      upsell_pd_1.classList.remove("hide");
    }

    if (pd2_validate == true) {
      upsell_pd_2.classList.add("hide");
    } else if (pd2_validate == false && pd_2_hdl !== "" && pd_2_hdl !== null) {
      upsell_pd_2.classList.remove("hide");
    }

    if (pd3_validate == true) {
      upsell_pd_3.classList.add("hide");
    } else if (pd3_validate == false && pd_3_hdl !== "" && pd_3_hdl !== null) {
      upsell_pd_3.classList.remove("hide");
    }

    if (active_hdl == active_hdl_in_cart) {
      upsell_title.classList.add("hide");
    } else {
      upsell_title.classList.remove("hide");
    }
  }


  // Free gift product in cart
  freeGiftInCart(cart) {
    if (typeof cart == "undefined") {
      (async () => {
        this.freeGiftInCart(await this.getCartData());
      })();
      return;
    }

    const freeGiftOverPrice = theme.fgincart.spendPrice;
    const fg_product_div = document.getElementById("freeGiftInCart");
    const variant_id = theme.fgincart.productVariantId;

    var freeGiftItemPrice = 0,
        itemDiscountTitle = new Object(),
        cartItems = cart.items,        
        cartTotal = cart.total_price,        
        fgItemInCart = false;    

    for (var i = 0; i < cartItems.length; i++) {
      if (cartItems[i].gift_card === true) {
        return;
      } 
      if ( cartItems[i].id === variant_id ) {
        fgItemInCart = true;        
        freeGiftItemPrice = cartItems[i].price;
        itemDiscountTitle = cartItems[i].discounts[0];        
      }
    }  

    if (fgItemInCart && cartTotal >= freeGiftOverPrice && itemDiscountTitle != undefined) {
      fg_product_div.style.display = 'none';
    } else {
      fg_product_div.removeAttribute('style');
    }
    
    if ( cartTotal >= freeGiftOverPrice && !fgItemInCart && itemDiscountTitle != undefined) {      
      var qty = 1;
      this.addItem(variant_id, qty);
      fg_product_div.style.display = 'none';      
    }
  }

    
 
  
}
